import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import api from '@utils/api';
import { usePublicConfig } from '@context/PublicConfigContext';
import MemoryTabs from './MemoryTabs';
import GalleryGrid from './GalleryGrid';
import AlbumCard from './AlbumCard';
import AlbumView from './AlbumView';
import MemorySkeleton from './MemorySkeleton';
import MemoryEmptyState from './MemoryEmptyState';
import PhotoViewer from './PhotoViewer';
import { normalizePhotoSrc } from './UploadPhotosDialog';

const SharedMemoriesSection = ({
  userId,
  isOwnProfile,
  accessDenied,
  hideHeading = false,
  embedded = false,
  tab: controlledTab,
  onTabChange,
}) => {
  const { siteOrigin } = usePublicConfig();
  const [internalTab, setInternalTab] = useState('all');
  const tab = controlledTab ?? internalTab;
  const setTab = onTabChange ?? setInternalTab;

  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const sentinelRef = useRef(null);

  const withAbsoluteUrls = useCallback(
    (list) =>
      (list || []).map((p) => ({
        ...p,
        url: normalizePhotoSrc(p.url, siteOrigin || ''),
      })),
    [siteOrigin],
  );

  const fetchPage = useCallback(
    async ({ reset = false, cursor = null } = {}) => {
      if (!userId || accessDenied) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      try {
        const { data } = await api.get(`/memories/users/${userId}/memories`, {
          params: {
            tab,
            ...(cursor ? { cursor } : {}),
          },
        });
        if (tab === 'albums') {
          setAlbums(
            (data.albums || []).map((a) => ({
              ...a,
              coverUrl: normalizePhotoSrc(a.coverUrl, siteOrigin || ''),
            })),
          );
          setPhotos([]);
          setNextCursor(null);
        } else {
          const page = withAbsoluteUrls(data.photos || []);
          setPhotos((prev) => (reset ? page : [...prev, ...page]));
          setAlbums([]);
          setNextCursor(data.nextCursor || null);
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          setPhotos([]);
          setAlbums([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [userId, accessDenied, tab, siteOrigin, withAbsoluteUrls],
  );

  useEffect(() => {
    setActiveAlbum(null);
    setViewerIndex(null);
    setPhotos([]);
    setAlbums([]);
    setNextCursor(null);
    fetchPage({ reset: true });
  }, [fetchPage]);

  useEffect(() => {
    if (tab === 'albums' || !nextCursor || loading) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMore) {
          fetchPage({ cursor: nextCursor });
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [tab, nextCursor, loading, loadingMore, fetchPage]);

  if (accessDenied) return null;

  const isEmpty =
    !loading &&
    ((tab === 'albums' && albums.length === 0) || (tab !== 'albums' && photos.length === 0));

  const body = (
    <>
      {activeAlbum ? (
        <AnimatePresence mode="wait">
          <AlbumView key={activeAlbum.activityId} album={activeAlbum} onBack={() => setActiveAlbum(null)} />
        </AnimatePresence>
      ) : (
        <>
          <MemoryTabs active={tab} onChange={setTab} isOwnProfile={isOwnProfile} />

          {loading ? (
            <MemorySkeleton variant={tab === 'albums' ? 'albums' : 'grid'} />
          ) : isEmpty ? (
            <MemoryEmptyState />
          ) : tab === 'albums' ? (
            <div className="space-y-3">
              {albums.map((album) => (
                <AlbumCard key={album.activityId} album={album} onClick={setActiveAlbum} />
              ))}
            </div>
          ) : (
            <>
              <GalleryGrid
                photos={photos}
                onPhotoClick={(p) => {
                  const i = photos.findIndex((x) => x._id === p._id);
                  setViewerIndex(i >= 0 ? i : 0);
                }}
              />
              <div ref={sentinelRef} className="h-4" />
              {loadingMore && (
                <p className="mt-2 text-center text-xs text-surface-400">Loading more…</p>
              )}
            </>
          )}
        </>
      )}

      {viewerIndex != null && (
        <PhotoViewer
          photos={photos}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onIndexChange={setViewerIndex}
          onDeleted={(id) => {
            setPhotos((prev) => prev.filter((p) => p._id !== id));
            setViewerIndex(null);
          }}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <section className="memory-section mt-4" id="shared-memories">
        {body}
      </section>
    );
  }

  return (
    <section className="memory-section pt-3 sm:pt-0" id="shared-memories">
      {!hideHeading && (
        <h3 className="mb-5 px-1 text-xs font-bold uppercase tracking-wider text-surface-500 dark:text-surface-400 sm:mb-3">
          Shared Memories
        </h3>
      )}
      <div className="rounded-3xl border border-[#E9EEF7] bg-white p-3 shadow-[0_2px_16px_rgba(15,23,42,0.04)] dark:border-white/[0.06] dark:bg-[#121826] dark:shadow-none sm:p-4">
        {body}
      </div>
    </section>
  );
};

export default SharedMemoriesSection;
